// The PURE half of the login / first-admin / create-user screens: form
// validation and the server-error-code -> Romanian message map. No DOM, no
// fetch, no three.js — so `auth-forms.test.mjs` can run it headless under
// `node --test`. The DOM lives next door in `lobby.ts`.
//
// Rules mirror the server (`src/server/auth/routes.ts`):
//   - email: permissive `x@y.z`, <= 254 chars, trimmed + lowercased
//   - password: at least 6 characters (MIN_PASSWORD)
// Validating client-side is a courtesy, never a gate: the server re-checks.

/** Same minimum as the server's MIN_PASSWORD. */
export const MIN_PASSWORD = 6

/** Which screen the user should see, decided by GET /api/auth/registration-open. */
export type AuthScreen = 'setup' | 'login'

export interface Credentials {
  email: string
  password: string
}

export interface ValidationOk {
  ok: true
  value: Credentials
}

export interface ValidationErr {
  ok: false
  /** Which field to focus/highlight. */
  field: 'email' | 'password'
  /** Ready-to-render Romanian message. */
  message: string
}

export type Validation = ValidationOk | ValidationErr

/** Deliberately permissive: this is a family app, not a mailing list. */
export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 254
}

/** Normalize an email the same way the server does before comparing. */
export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

/**
 * Validate a login / register / create-user form. Returns the normalized
 * credentials, or the first problem with a friendly Romanian message.
 */
export function validateCredentials(emailInput: string, passwordInput: string): Validation {
  const email = normalizeEmail(emailInput)
  if (email.length === 0) {
    return { ok: false, field: 'email', message: 'Scrie adresa de email. ✉️' }
  }
  if (!isEmail(email)) {
    return { ok: false, field: 'email', message: 'Emailul nu arată bine. Exemplu: ana@exemplu.ro' }
  }
  if (passwordInput.length === 0) {
    return { ok: false, field: 'password', message: 'Scrie o parolă. 🔑' }
  }
  if (passwordInput.length < MIN_PASSWORD) {
    return {
      ok: false,
      field: 'password',
      message: `Parola are nevoie de cel puțin ${MIN_PASSWORD} caractere.`
    }
  }
  return { ok: true, value: { email, password: passwordInput } }
}

/** Server error codes (the `error` field of a non-2xx JSON body) -> Romanian. */
const ERROR_MESSAGES: Record<string, string> = {
  invalid_login: 'Email sau parolă greșită. Mai încearcă! 🙂',
  invalid_credentials: 'Emailul sau parola nu sunt bune. Verifică-le și mai încearcă.',
  registration_closed: 'Contul de administrator există deja. Intră cu emailul tău.',
  email_taken: 'Există deja un cont cu acest email.',
  not_authenticated: 'Nu ești conectat. Intră în cont mai întâi.',
  forbidden: 'Doar administratorul poate face asta.',
  network: 'Serverul nu răspunde. Verifică conexiunea. ❌'
}

/** Fallback for a code we've never seen — never show the raw code to a kid. */
const FALLBACK_MESSAGE = 'Ceva n-a mers. Mai încearcă o dată. 🤔'

/**
 * Map a server error code (or anything else) to a Romanian message. Unknown or
 * missing codes fall back to a friendly generic line, never the raw code.
 */
export function describeError(code: unknown): string {
  if (typeof code !== 'string') return FALLBACK_MESSAGE
  return ERROR_MESSAGES[code] ?? FALLBACK_MESSAGE
}

/** Pick the screen from the /api/auth/registration-open answer. */
export function screenFor(registrationOpen: boolean): AuthScreen {
  return registrationOpen ? 'setup' : 'login'
}

export interface ScreenCopy {
  title: string
  subtitle: string
  submit: string
}

/** All user-visible strings for a screen, in one place (Romanian). */
export function screenCopy(screen: AuthScreen): ScreenCopy {
  if (screen === 'setup') {
    return {
      title: 'Bun venit în Voxelcraft! 🧱',
      subtitle: 'Ești primul aici — creează contul de administrator.',
      submit: 'Creează contul de administrator'
    }
  }
  return {
    title: 'Voxelcraft ⛏️',
    subtitle: 'Intră în cont ca să construiești.',
    submit: 'Intră în joc'
  }
}
